import { isEmpty } from 'lodash'
import { useCallback, useState } from 'react'

export type HeadCellStatus = 'all' | 'empty' | 'partial'

type ID = string

export type UseRowSelection = {
  headCellStatus: HeadCellStatus
  isChecked: (rowId: ID) => boolean
  selectedRows: ID[]
  setSelectedRows: React.Dispatch<React.SetStateAction<ID[]>>
  toggle: (rowId: ID) => void
  toggleHeadCell: (allRows: Array<ID>) => void
}

type Props = {
  totalRows: number
  defaultSelectedRows?: Array<ID>
}

export const useRowSelection = ({ totalRows, defaultSelectedRows }: Props) => {
  const [selectedRows, setSelectedRows] = useState<Array<ID>>(defaultSelectedRows ?? [])

  const toggle = useCallback((rowId: ID) => {
    setSelectedRows((previousSelectedRows) => {
      if (previousSelectedRows.includes(rowId)) {
        return previousSelectedRows.filter(
          (previousRowId) => previousRowId !== rowId
        )
      }
      return [...previousSelectedRows, rowId]
    })
  }, [])

  const isChecked = useCallback(
    (rowId: ID) => selectedRows.includes(rowId),
    [selectedRows]
  )

  const headCellStatus: HeadCellStatus =
    selectedRows.length === totalRows
      ? 'all'
      : isEmpty(selectedRows)
        ? 'empty'
        : 'partial'

  const toggleHeadCell = useCallback(
    (allRows: Array<ID>) =>
      isEmpty(selectedRows) ? setSelectedRows(allRows) : setSelectedRows([]),
    [selectedRows]
  )

  return {
    headCellStatus,
    isChecked,
    selectedRows,
    setSelectedRows,
    toggle,
    toggleHeadCell,
  }
}
